import { S3Client } from "@aws-sdk/client-s3";
import type { S3ClientConfig } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import type { AwsRegionConfig } from "../config/aws-regions";
import { isCredentialError } from "../recorder/core/upload/credential-lifecycle";

export { isCredentialError };

export interface CognitoCredentialsProvider {
  getClient: () => Promise<S3Client>;
  isCredentialError: (error: unknown) => boolean;
}

/**
 * Per-instance factory — each call returns an isolated closure so concurrent uploads don't share state.
 */
export function createCognitoCredentialsProvider(
  config: AwsRegionConfig,
): CognitoCredentialsProvider {
  let s3Client: S3Client | null = null;

  const initialize = async (): Promise<S3Client> => {
    const clientConfig: S3ClientConfig = {
      region: config.region,
      credentials: fromCognitoIdentityPool({
        // clientConfig lets the provider construct its own CognitoIdentityClient,
        // avoiding a type mismatch with externally constructed instances.
        clientConfig: { region: config.region },
        identityPoolId: config.identityPoolId,
      }),
      // WHEN_REQUIRED: S3 rejects MD5 checksums by default in 2024+ SDK; only compute when the spec requires it.
      requestChecksumCalculation: "WHEN_REQUIRED",
      responseChecksumValidation: "WHEN_REQUIRED",
    };
    s3Client = new S3Client(clientConfig);
    return s3Client;
  };

  const getClient = async (): Promise<S3Client> => {
    if (s3Client) return s3Client;
    return initialize();
  };

  return { getClient, isCredentialError };
}

/**
 * Session-scoped S3 client cache, keyed by `${region}:${identityPoolId}`.
 *
 * Safe across logout: the pool is unauthenticated, so these are guest credentials not bound to the
 * signed-in user. An authenticated identity would require re-reviewing this.
 */
const clientCache = new Map<string, Promise<S3Client>>();

const cacheKey = (config: AwsRegionConfig): string => `${config.region}:${config.identityPoolId}`;

export function getCachedCognitoClient(config: AwsRegionConfig): Promise<S3Client> {
  const key = cacheKey(config);
  const cached = clientCache.get(key);
  if (cached) return cached;

  const pending = createCognitoCredentialsProvider(config).getClient();
  clientCache.set(key, pending);
  // A failed mint must not be cached; guarded so a late rejection cannot evict a newer entry.
  pending.catch(() => {
    if (clientCache.get(key) === pending) clientCache.delete(key);
  });
  return pending;
}

/** Drops the cached client and mints a replacement. Compare-and-swap so concurrent callers converge on one. */
export function invalidateCognitoClient(
  config: AwsRegionConfig,
  stale?: Promise<S3Client>,
): Promise<S3Client> {
  const key = cacheKey(config);
  if (!stale || clientCache.get(key) === stale) clientCache.delete(key);
  return getCachedCognitoClient(config);
}
